import { pipe, Array as A, Option as O } from 'effect'
import { useEffect, useState } from 'react'
import { CircleInfoIcon, FlashIcon } from '@repo/ui'
import type { Bet } from 'src/types/domain/Bet'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import AngleDoubleRight from 'src/app/assets/icons/ico-angle-double-right.svg'
import Ellipsis from 'src/app/assets/icons/ico-ellipsis.svg'
import EllipsisAlt from 'src/app/assets/icons/ico-ellipsis-alt.svg'
import { getOrdinalSuffix } from 'src/utils/getOrdinalSuffix'
import { BetClassNameMap, getVisualPotentialPayout } from 'src/utils/betUtils'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Marble } from 'src/types/domain/Marble'
import type { RaceSettledEvent } from 'src/types/domain/RaceEvent'
import { isRaceSettledEvent } from 'src/types/domain/RaceEvent'
import { displayMoney } from 'src/utils/displayAmount'
import { useClientSideTranslation } from 'src/services/i18n'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { useGetCurrentLanguage } from 'src/store/game'
import { BetTypeLabel } from 'src/components/BetTypeLabel/BetTypeLabel'
import { MarblePositionLabel } from 'src/components/MarblePositionLabel'
import { useGetPlayerChannel } from 'src/store/socket'
import { useGetCurrentPlayer } from 'src/store/player'
import { usePayoutCalculation } from 'src/hooks/usePayoutCalculation'
import { useSoundActions } from 'src/store/sound'
import { useBonusDisplay } from 'src/hooks/useBonusDisplay'
import { useInventoryStore } from 'src/features/inventory/store'
import { isBonus } from 'src/features/inventory/types/domain'
import { MarbleItem } from '../../MarbleItem'
import { BetPayoutLabel } from './BetPayoutLabel'

type Props = Readonly<{
  game: MarbleGame
  currentBet?: Bet
  isNextRaceBet?: boolean
}>

export default function CurrentBetPane({ currentBet, game, isNextRaceBet = false }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const currentPlayer = useGetCurrentPlayer()
  const playerChannel = useGetPlayerChannel()
  const soundActions = useSoundActions()

  const [bonusPotentialWin, setBonusPotentialWin] = useState<string | undefined>()
  const [showBonusValue, setShowBonusValue] = useState(false)

  const raceSettledEvent = game.races[0].raceEvents.find(isRaceSettledEvent)
  const calculatePayout = usePayoutCalculation({ playerChannel, player: currentPlayer, game })
  const { startAutoHideTimer, clearHideTimeout } = useBonusDisplay()

  // Get inventory items to check if any attached items are free bet bonuses (not just boosters)
  const inventoryItems = useInventoryStore((state) => state.items)
  const isFreeBet =
    currentBet?.betDetails.inventoryItemIds?.some((itemId) => {
      const item = inventoryItems.find((i) => i.id === itemId)
      return item && isBonus(item)
    }) ?? false

  useEffect(() => {
    if (!currentBet) return

    const updatePayout = async () => {
      const selectedMarbles = currentBet.betDetails.selectedItems
        .map((marbleId) => game.marbles.find((m: Marble) => m.uuid === marbleId))
        .filter((m: Marble | undefined) => m !== undefined)

      const payoutResult = await calculatePayout({
        betAmount: currentBet.betAmount.toString(),
        selectedMarbles,
        betType: currentBet.betDetails.type,
        inventoryItemIds: currentBet.betDetails.inventoryItemIds,
        // Pass roundId to include lightning boost in bonus calculation
        roundId: currentBet.raceId
      })

      if (payoutResult?.bonusPayout) {
        setBonusPotentialWin(payoutResult.bonusPayout.toString())
      } else {
        setBonusPotentialWin(undefined)
      }
    }

    void updatePayout()
  }, [calculatePayout, currentBet, game.marbles])

  if (!currentBet) {
    return null
  }

  const renderBetMarbles = (bet: Bet, raceSettled: RaceSettledEvent | undefined) => {
    const betInExactOrder = ['StraightForecast', 'Tricast'].includes(bet.betDetails.type)
    const selectedMarbles = bet.betDetails.selectedItems
      .map((marbleId) => game.marbles.find((m: Marble) => m.uuid === marbleId))
      .filter((m: Marble | undefined) => m !== undefined)
    if (selectedMarbles.length === 0) return null

    // Special layout for irl_alt/crash - show icon with label
    if (
      (game.track.gameType === 'irl_alt' ||
        game.track.gameType === 'duck_river' ||
        game.track.gameType === 'snow_run' ||
        game.track.gameType === 'social_betting' ||
        game.track.gameType === 'crash') &&
      selectedMarbles.length === 1
    ) {
      const marble = selectedMarbles[0]
      if (!marble) return null
      return (
        <div className="irl-alt-bet-display">
          <MarbleItem className={`marble-${marble.color}`} marble={marble} />
          <span className="irl-alt-bet-label">{getTranslatedMarbleColor(marble.color, t)}</span>
        </div>
      )
    }

    if (bet.betDetails.type === 'PickWinner') {
      return selectedMarbles.map((marble) => {
        const { isWinningMarble } = pipe(
          O.fromNullable(raceSettled),
          O.map((result) => {
            return result.payload.results
          }),
          O.flatMap((results) =>
            pipe(
              results,
              A.findFirst((result) => {
                return result.marbleId === marble.uuid
              }),
              O.map((result) => {
                return {
                  position: result.position,
                  positionSuffix: getOrdinalSuffix(Number(result.position)),
                  isWinningMarble: result.position === 1
                }
              })
            )
          ),
          O.getOrElse(() => ({
            position: undefined,
            positionSuffix: '',
            isWinningMarble: false
          }))
        )

        return (
          <MarbleItem
            className={`${isWinningMarble ? 'winning-marble' : ''} marble-${marble.color}`}
            key={marble.number}
            marble={marble}
            tooltip={getTranslatedMarbleColor(marble.color, t)}
          />
        )
      })
    }
    return (
      <div className="choose-marbles-order show-single-position-compact">
        {selectedMarbles.map((marble) => {
          const position = bet.betDetails.selectedItems.indexOf(marble.uuid) + 1

          return (
            <div
              className="entry-order entry-order-marble-spin"
              key={`${bet.betDetails.type}${marble.uuid}`}
            >
              <div className="entry-marble">
                <MarbleItem
                  className={`marble-${marble.color}`}
                  marble={marble}
                  tooltip={getTranslatedMarbleColor(marble.color, t)}
                />
              </div>
              {betInExactOrder ? (
                <div className="entry-selector">
                  <div className={`select ${getOrdinalSuffix(position)} selected`}>
                    <MarblePositionLabel position={position} />
                  </div>
                </div>
              ) : (
                <div className="entry-selector">
                  <div className="select">
                    {selectedMarbles.length === 2 ? <EllipsisAlt /> : <Ellipsis />}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="marbles-bet-container">
      <div className="std-table two-rows-multiple-columns">
        <div className="std-table__inner">
          <div className="std-table__inner-body current-bet-pane">
            <div className={`entry ${BetClassNameMap[currentBet.status]}`} key={currentBet.id}>
              <div className="top-bar">
                <div className={`bet-entry ${BetClassNameMap[currentBet.status]}`}>
                  <div>
                    <span className={`bet-entry__value${isFreeBet ? ' has-free-bet' : ''}`}>
                      {getCurrencySymbol(currentBet.currency)}
                      {displayMoney(currentBet.betAmount.toString(), true)}
                    </span>
                    <span className="bet-entry__label">
                      {isFreeBet ? t('Free Bet Amount') : t('Bet Amount')}
                    </span>
                  </div>
                  <div className="bet-entry__arrow">
                    <AngleDoubleRight />
                  </div>
                  <div className={`${BetClassNameMap[currentBet.status]}`}>
                    <div className="bet-entry__wrapper">
                      {BetClassNameMap[currentBet.status] !== 'lost' && (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- The span is used as a clickable element for toggling bonus payout, but keyboard events are handled via tabIndex and role for accessibility.
                        <span
                          className={`bet-entry__value ${bonusPotentialWin ? 'has-bonus' : ''}`}
                          onClick={() => {
                            if (bonusPotentialWin) {
                              soundActions.playEffect('click')
                              if (showBonusValue) {
                                // If already visible, hide it
                                clearHideTimeout()
                                setShowBonusValue(false)
                              } else {
                                // Show bonus value and start auto-hide timer
                                setShowBonusValue(true)
                                startAutoHideTimer(() => {
                                  setShowBonusValue(false)
                                })
                              }
                            }
                          }}
                          role={bonusPotentialWin ? 'button' : undefined}
                        >
                          {showBonusValue && bonusPotentialWin ? (
                            <>
                              <span className="flash-icon icon">
                                <FlashIcon className="w-2 h-2 text-white" />
                              </span>
                              +{getCurrencySymbol(currentBet.currency)}
                              {displayMoney(bonusPotentialWin, true)}
                            </>
                          ) : (
                            <>
                              {getCurrencySymbol(currentBet.currency)}
                              {getVisualPotentialPayout(currentBet, currentBet.status)}

                              {bonusPotentialWin ? (
                                <span className="icon info-icon">
                                  <CircleInfoIcon className="w-2 h-2 text-white" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </span>
                      )}
                    </div>

                    <BetPayoutLabel currentBetStatus={currentBet.status} />
                  </div>
                </div>
              </div>
              <div
                className={`bottom-bar bottom-bar__current-bet ${game.track.gameType === 'irl_alt' || game.track.gameType === 'duck_river' || game.track.gameType === 'snow_run' || game.track.gameType === 'social_betting' || game.track.gameType === 'crash' ? 'irl-alt-layout' : ''}`}
              >
                <div className="left">
                  <span className={`label ${currentBet.betDetails.type}`}>
                    {isNextRaceBet ? t('Next Game Bet') : t('Current Bet')}
                  </span>
                  {game.track.gameType !== 'irl_alt' &&
                    game.track.gameType !== 'duck_river' &&
                    game.track.gameType !== 'snow_run' &&
                    game.track.gameType !== 'social_betting' &&
                    game.track.gameType !== 'crash' && (
                      <BetTypeLabel
                        betType={currentBet.betDetails.type}
                        className="bottom-bar__current-bet__label"
                      />
                    )}
                </div>
                <div className={`bottom-bar__current-bet__content ${currentBet.betDetails.type}`}>
                  <div className="marbles">{renderBetMarbles(currentBet, raceSettledEvent)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
